import { useEffect, useRef, useState } from "react";
import { IDENTITY, PROJECTS, SKILLS, CERTIFICATES } from "@/data/content";
import { playKeyClick, playBeep } from "@/lib/sound";

const PROMPT = "wiki@wikios:~$";

const BANNER = [
    { text: "wikiOS 26.04 LTS · tty1", cls: "text-neutral-500" },
    { text: "Last login: today, uptime 99.98%", cls: "text-neutral-500" },
    { text: "type 'help' to list available commands", cls: "text-neutral-300" },
];

const COMMANDS = {
    help: () => [
        { text: "available commands:", cls: "text-vermilion font-bold" },
        ...["whoami", "about", "skills", "projects", "certs", "contact", "socials", "ping", "uptime", "neofetch", "matrix", "clear"].map((c) => ({
            text: `  ${c}`, cls: "text-neutral-300",
        })),
    ],
    whoami: () => [
        { text: IDENTITY.name, cls: "text-white font-bold" },
        { text: `${IDENTITY.role} — ${IDENTITY.location}`, cls: "text-neutral-300" },
        { text: IDENTITY.status, cls: "text-neutral-500" },
    ],
    about: () => [
        { text: "6+ years as the person teams call when nothing works.", cls: "text-neutral-300" },
        { text: "Plan, build & babysit infrastructure so the company can pretend IT is magic.", cls: "text-neutral-300" },
    ],
    skills: () =>
        SKILLS.flatMap((g) => [
            { text: `[${g.number}] ${g.title}`, cls: "text-vermilion" },
            { text: `     ${g.items.join(" · ")}`, cls: "text-neutral-400" },
        ]),
    projects: () =>
        PROJECTS.map((p) => ({
            text: `  ${p.number}. ${p.title} — ${p.client}`, cls: "text-neutral-300",
        })),
    certs: () =>
        CERTIFICATES.map((c) => ({
            text: `  ${c.id} · ${c.title} (${c.issuer})`, cls: "text-neutral-300",
        })),
    contact: () => [
        { text: `  email → ${IDENTITY.email}`, cls: "text-white" },
        { text: "  or open the Mail app on the desktop", cls: "text-neutral-400" },
    ],
    socials: () => [
        { text: `  github   → ${IDENTITY.github}`, cls: "text-white" },
        { text: `  linkedin → ${IDENTITY.linkedin}`, cls: "text-white" },
        { text: `  resume   → ${IDENTITY.resume}`, cls: "text-white" },
    ],
    ping: () => [
        { text: "PING jakarta.node (103.147.8.42): 56 data bytes", cls: "text-neutral-400" },
        { text: "64 bytes: icmp_seq=0 ttl=57 time=18.2 ms", cls: "text-neutral-300" },
        { text: "64 bytes: icmp_seq=1 ttl=57 time=17.6 ms", cls: "text-neutral-300" },
        { text: "--- 0% packet loss · all systems nominal ---", cls: "text-vermilion" },
    ],
    uptime: () => [
        { text: ` ${new Date().toLocaleTimeString("en-GB")} up 6 years, 34 systems, load average: 0.14, 0.08, 0.05`, cls: "text-neutral-300" },
    ],
    neofetch: () => [
        { text: "        ██████        wiki@wikios", cls: "text-vermilion" },
        { text: "      ██      ██      -----------", cls: "text-vermilion" },
        { text: "      ██  ██  ██      OS: wikiOS 26.04 LTS x86_64", cls: "text-vermilion" },
        { text: "      ██      ██      DE: Portfolio Shell", cls: "text-red-400" },
        { text: "        ██████        Shell: bash 5.2 + tmux", cls: "text-red-400" },
        { text: "                     Stack: Docker · MikroTik · Grafana · Laravel · Next.js", cls: "text-red-400" },
        { text: "                     Uptime: 99.98% · Coffee: critical", cls: "text-amber-400" },
    ],
    matrix: () => [
        { text: "01010100 01101000 01100101 01110010 01100101", cls: "text-emerald-500/80" },
        { text: "01101001 01110011 00100000 01101110 01101111", cls: "text-emerald-500/60" },
        { text: "…wake up, the servers have you. follow the green dashboard.", cls: "text-emerald-400" },
    ],
    sudo: () => [{ text: "wiki is not in the sudoers file. This incident will be reported. (nice try)", cls: "text-red-400" }],
};

export default function TerminalWindow() {
    const [history, setHistory] = useState(BANNER);
    const [input, setInput] = useState("");
    const bodyRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 250);
    }, []);

    useEffect(() => {
        bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
    }, [history]);

    const run = (raw) => {
        const cmd = raw.trim().toLowerCase();
        const echo = { text: `${PROMPT} ${raw}`, cls: "text-white" };
        if (!cmd) {
            setHistory((h) => [...h, echo]);
            return;
        }
        if (cmd === "clear") {
            setHistory([]);
            return;
        }
        const fn = COMMANDS[cmd];
        const output = fn ? fn() : [{ text: `bash: ${cmd}: command not found — try 'help'`, cls: "text-red-400" }];
        setHistory((h) => [...h, echo, ...output]);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            playBeep(520, 0.06, 0.06, "square");
            run(input);
            setInput("");
            return;
        }
        if (e.key.length === 1 || e.key === "Backspace") playKeyClick();
    };

    return (
        <div
            ref={bodyRef}
            data-testid="terminal-body"
            onClick={() => inputRef.current?.focus()}
            className="h-full overflow-y-auto p-4 font-mono text-[13px] leading-relaxed cursor-text bg-[#0c0e12]"
        >
            {history.map((line, i) => (
                <div key={i} className={`whitespace-pre-wrap ${line.cls}`}>
                    {line.text}
                </div>
            ))}
            <div className="flex items-center gap-2 mt-1">
                <span className="text-vermilion shrink-0">{PROMPT}</span>
                <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    data-testid="terminal-input"
                    aria-label="Terminal command input"
                    className="flex-1 bg-transparent outline-none text-white caret-vermilion placeholder:text-neutral-700"
                    placeholder="type a command…"
                    autoComplete="off"
                    spellCheck={false}
                />
            </div>
        </div>
    );
}
